import { CircleDetails, CustomShape, GeoJsonFeature, GeoJsonGeometry, GeoJsonType, ObjectId, ProcessingOutput, TopLevelShapeHolder } from '../types';

import circle from '@turf/circle';
import destination from '@turf/destination';
import { getMainPage } from './page-name';
import { isDefined } from './utils';
import topicData from './state/topic-data';

export function googleShapeToGeoJsonData(overlay: any, type: any): ProcessingOutput {
      let geometry: GeoJsonGeometry;
      let circle_details: CircleDetails | undefined;
      const shape = overlay;
      let shapeType = type;

      // if shape type is polyline but the first and last points are the same, then it is a polygon
      if (shapeType === google.maps.drawing.OverlayType.POLYLINE || shapeType === google.maps.Polyline) {
          const check_path = shape.getPath();
          if (check_path.getLength() > 2 && check_path.getAt(0).equals(check_path.getAt(check_path.getLength() - 1)) === true) {
              shapeType = google.maps.drawing.OverlayType.POLYGON;
          }
      }

      if (shapeType === google.maps.drawing.OverlayType.POLYGON || shapeType === google.maps.Polygon || shapeType === 'Polygon') {
          const path = shape.getPath();
          const coordinates = path.getArray().map((latLng: { lng: () => any; lat: () => any; }) => {
              return [latLng.lng(), latLng.lat()];
          });

          // close the polygon
          coordinates.push(coordinates[0]);
           geometry = {
              type: GeoJsonType.Polygon,
              coordinates: [coordinates],
          };
      } else if (shapeType === google.maps.drawing.OverlayType.POLYLINE || shapeType === google.maps.Polyline || shapeType === 'LineString') {
          // if it is
          const path = shape.getPath();
          const coordinates = path.getArray().map((latLng: { lng: () => any; lat: () => any; }) => {
              return [latLng.lng(), latLng.lat()];
          });
          geometry = {
              type: GeoJsonType.LineString,
              coordinates,
          };
      } else if (shapeType === google.maps.drawing.OverlayType.CIRCLE || shapeType === google.maps.Circle || shapeType === 'Circle') {
          const center = shape.getCenter() as google.maps.LatLng;
          const radius = shape.getRadius();
          const feature = circle([center.lng(), center.lat()], radius / 1000, { steps: 64, units: 'kilometers' });
          const coordinates = feature.geometry.coordinates[0].map((coord: any) => {
              return [coord[0], coord[1]];
          } );
          geometry = {
              type: GeoJsonType.Polygon,
              coordinates: [coordinates],
          };
          circle_details = {
              center:{
                  lat: center.lat(),
                  lng: center.lng(),
              },
              radius_m: radius,
          };
      } else if (shapeType === google.maps.drawing.OverlayType.RECTANGLE || shapeType === google.maps.Rectangle || shapeType === 'Rectangle') {
          const bounds = shape.getBounds();
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const coordinates = [
              [ne.lng(), ne.lat()],
              [ne.lng(), sw.lat()],
              [sw.lng(), sw.lat()],
              [sw.lng(), ne.lat()],
              [ne.lng(), ne.lat()],
          ];
          geometry = {
              type: GeoJsonType.Polygon,
              coordinates: [coordinates],
          };
      } else {
        console.error('Unsupported shape type');
        throw new Error('Unsupported shape type');
      }
      return { geometry , circle_details };
    }

    export function createShapeHolderFromCustomShape(shape: CustomShape): TopLevelShapeHolder {
        return {
        shape,
        id: shape._id?.$oid ?? undefined,
        inner: createGoogleMapShapeFromCustomShape(shape),
        };
    }

    export async function googleFeatureToCustomShape(feature: google.maps.Data.Feature): Promise<CustomShape> {
      const geojson = await getShapeGeoJson(feature);
      return {
        // get random number is bad. need to use other method
        _id: geojson.properties._id as ObjectId,
        name: geojson.properties.name as string ?? '',
        description: geojson.properties.description as string ?? '',
        geojson: geojson.geometry,
        style: {
            stroke_color: geojson.properties.stroke_color as string ?? topicData.topic?.default_shape_style?.stroke_color ?? '#000000',
            stroke_weight: geojson.properties.stroke_weight as number ?? topicData.topic?.default_shape_style?.stroke_weight ?? 1,
            stroke_opacity: geojson.properties.strokeOpacity as number ?? topicData.topic?.default_shape_style?.stroke_opacity ?? 1,
            fill_color: geojson.properties.fill_color as string ?? topicData.topic?.default_shape_style?.fill_color ?? '#000000',
            fill_opacity: geojson.properties.fill_opacity as number ?? topicData.topic?.default_shape_style?.fill_opacity ?? 0.5,
        }
      };
    }

    export async function getShapeGeoJson(feature: { toGeoJson: (arg0: (geoJson: any) => void) => void; }): Promise<GeoJsonFeature> {
        return await new Promise((resolve, reject) => {
            feature.toGeoJson((geoJson: any) => {
                resolve(geoJson);
            });
        });
    }

function convertCoordinates(coordinates: any[]): Array<{ lat: number, lng: number }> {
    return coordinates.map((coord: any) => {
        return { lat: coord[1], lng: coord[0] };
    });
}

function createPolygon(coords: any[], style: any, clickable: boolean, editable: boolean): google.maps.Polygon {
    return new google.maps.Polygon({
        paths: coords,
        strokeColor: style?.stroke_color ?? '#000000',
        strokeOpacity: style?.stroke_opacity ?? 1,
        strokeWeight: style?.stroke_weight ?? 1,
        fillColor: style?.fill_color ?? '#000000',
        fillOpacity: style?.fill_opacity ?? 0.5,
        clickable,
        editable,
    });
}

function createPolyline(coords: any[], style: any, clickable: boolean, editable: boolean): google.maps.Polyline {
    return new google.maps.Polyline({
        path: coords,
        strokeColor: style?.stroke_color ?? '#000000',
        strokeOpacity: style?.stroke_opacity ?? 1,
        strokeWeight: style?.stroke_weight ?? 1,
        clickable,
        editable,
    });
}

export function createGoogleMapShapeFromCustomShape(shape: CustomShape): google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> {
    let coords: any[] = [];
    const topicStyle = topicData.topic?.default_shape_style;
    if (shape.geojson.type === 'Polygon' && shape.geojson.coordinates && Array.isArray(shape.geojson.coordinates[0])) {
        coords = convertCoordinates(shape.geojson.coordinates[0]);
        return createPolygon(coords, shape.style ?? topicStyle, true, false);
    } else if (shape.geojson.type === 'LineString' && shape.geojson.coordinates) {
        coords = convertCoordinates(shape.geojson.coordinates);
        return createPolyline(coords, shape.style ?? topicStyle, true, false);
    } else if (shape.geojson.type === 'MultiPolygon' && shape.geojson.coordinates) {
        const polygons: google.maps.Polygon[] = shape.geojson.coordinates.map((polygon: any) => {
            coords = convertCoordinates(polygon[0]);
            return createPolygon(coords, shape.style ?? topicStyle, true, false);
        });
        return polygons;
    } else if (shape.geojson.type === 'MultiLineString' && shape.geojson.coordinates) {
        const polylines: google.maps.Polyline[] = shape.geojson.coordinates.map((line: any) => {
            coords = convertCoordinates(line);
            return createPolyline(coords, shape.style ?? topicStyle, true, false);
        });
        return polylines;
    } else if (shape.geojson.type === 'GeometryCollection' && shape.geojson.coordinates) {
        const newShapes: Array<google.maps.Polygon | google.maps.Polyline> = [];
        shape.geojson.coordinates.forEach((geometry: any) => {
            if (geometry.type === 'Polygon' && isDefined(geometry.coordinates) && Array.isArray(geometry.coordinates[0])) {
                coords = convertCoordinates(geometry.coordinates[0]);
                newShapes.push(createPolygon(coords, shape.style ?? topicStyle, true, false));
            } else if (geometry.type === 'LineString' && isDefined(geometry.coordinates)) {
                coords = convertCoordinates(geometry.coordinates);
                newShapes.push(createPolyline(coords, shape.style ?? topicStyle, true, false));
            } else if (geometry.type === 'MultiPolygon' && isDefined(geometry.coordinates)) {
                const polygons: google.maps.Polygon[] = geometry.coordinates.map((polygon: any) => {
                    coords = convertCoordinates(polygon[0]);
                    return createPolygon(coords, shape.style ?? topicStyle, true, false);
                });
                newShapes.push(...polygons);
            } else if (geometry.type === 'MultiLineString' && isDefined(geometry.coordinates)) {
                const polylines: google.maps.Polyline[] = geometry.coordinates.map((line: any) => {
                    coords = convertCoordinates(line);
                    return createPolyline(coords, shape.style ?? topicStyle, true, false);
                });
                newShapes.push(...polylines);
            } else {
                console.error(geometry);
            }
        });
        return newShapes;
    }
    console.error('Unsupported shape type for', shape.name);
    console.error(shape.geojson);
    return [];
}

export function makeShapeEditable(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined, editable: boolean): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setEditable(editable);
        });
    } else {
        shape.setEditable(editable);
    }
}

export function addShapeToMap(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined, map: google.maps.Map): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setMap(map);
        });
    } else {
        shape.setMap(map);
    }
}

export function removeShapeFromMap(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setMap(null);
        });
    } else {
        shape.setMap(null);
    }
}

export function showShape(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setVisible(true);
        });
    } else {
        shape.setVisible(true);
    }
}

export function hideShape(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setVisible(false);
        });
    } else {
        shape.setVisible(false);
    }
}

export function makeShapeDraggable(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined, draggable: boolean): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setDraggable(draggable);
        });
    } else {
        shape.setDraggable(draggable);
    }
}

export function setShapeOptions(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined, options: any): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setOptions(options);
        });
    } else {
        shape.setOptions(options);
    }
}

export function addListenerToShape(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined, event: string, callback: (...args: any[]) => void | Promise<void>): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.addListener(event, callback);
        });
    } else {
        shape.addListener(event, callback);
    }
}

export function changeShapeClickable(shape: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined, clickable: boolean): void {
    if (!shape) {
        return;
    }
    if (Array.isArray(shape)) {
        shape.forEach((s) => {
            s.setOptions({ clickable });
        });
    } else {
        shape.setOptions({ clickable });
    }
}

export function getShapeIconName(type: string): string {
    switch (type) {
      case 'Polygon':
        return 'change_history';
      case 'LineString':
        return 'polyline';
      case 'Circle':
        return 'trip_origin';
      default:
        return 'polyline';
    }
  }

export function metersToMiles(meters: number) {
    return meters * 0.000621371;
}

export function milesToMeters(miles: number) {
    return miles / 0.000621371;
}

export function feetToMeters(feet: number) {
    return feet * 0.3048;
}

export function metersToFeet(meters: number) {
    return meters / 0.3048;
}

export function yardsToMeters(yards: number) {
    return yards * 0.9144;
}

export function metersToYards(meters: number) {
    return meters / 0.9144;
}

export type PreciseShapeUnit = 'meters' | 'feet' | 'yards' | 'miles';

export function toMeters(value: number, unit: PreciseShapeUnit): number {
    switch (unit) {
        case 'feet': return feetToMeters(value);
        case 'yards': return yardsToMeters(value);
        case 'miles': return milesToMeters(value);
        default: return value;
    }
}

export function createPreciseRectangle(center: [number, number], widthMeters: number, heightMeters: number): GeoJsonGeometry {
    const halfW = widthMeters / 2 / 1000;
    const halfH = heightMeters / 2 / 1000;

    const north = destination(center, halfH, 0, { units: 'kilometers' });
    const south = destination(center, halfH, 180, { units: 'kilometers' });
    const ne = destination(north.geometry.coordinates as [number, number], halfW, 90, { units: 'kilometers' });
    const nw = destination(north.geometry.coordinates as [number, number], halfW, 270, { units: 'kilometers' });
    const se = destination(south.geometry.coordinates as [number, number], halfW, 90, { units: 'kilometers' });
    const sw = destination(south.geometry.coordinates as [number, number], halfW, 270, { units: 'kilometers' });

    const coordinates: Array<[number, number]> = [
        ne.geometry.coordinates as [number, number],
        se.geometry.coordinates as [number, number],
        sw.geometry.coordinates as [number, number],
        nw.geometry.coordinates as [number, number],
        ne.geometry.coordinates as [number, number],
    ];

    return {
        type: GeoJsonType.Polygon,
        coordinates: [coordinates],
    };
}

export function createPreciseLine(start: [number, number], lengthMeters: number, bearingDeg: number): GeoJsonGeometry {
    const end = destination(start, lengthMeters / 1000, bearingDeg, { units: 'kilometers' });
    return {
        type: GeoJsonType.LineString,
        coordinates: [start, end.geometry.coordinates as [number, number]],
    };
}

export function countVertices(coordinates: any[]): number {
    return coordinates.flat(2).length;
}

export function codeToCustomShapes(jsonCode: string, originalShapes?: CustomShape[]) : CustomShape[] {
    const jsonData = JSON.parse(jsonCode);
    let featuresCollection = [];
    if (jsonData.type === 'Feature') {
        featuresCollection = [jsonData];
    } else if (jsonData.type === 'FeatureCollection') {
        featuresCollection = jsonData.features;
    } else {
        getMainPage().errorToastElement.showToastAndThrow('Invalid GeoJSON code');
    }

    let customShapes = originalShapes ?? [];
    featuresCollection.forEach((newFeature: any, index: number) => {
        const id = newFeature.properties?._id?.$oid;
        if (isDefined(id) && newFeature.properties?._id !== 'preview') {
            customShapes = customShapes.map((shape: CustomShape) => {
                if (shape._id?.$oid === id) {
                    return {
                        _id: shape._id,
                        name: newFeature.properties.name,
                        description: newFeature.properties.description,
                        geojson: {
                            type: newFeature.geometry.type,
                            coordinates: newFeature.geometry.coordinates ?? newFeature.geometry.geometries,
                        },
                        style: {
                            stroke_color: newFeature.properties.stroke_color,
                            stroke_weight: newFeature.properties.stroke_weight,
                            stroke_opacity: newFeature.properties.stroke_opacity,
                            fill_color: newFeature.properties.fill_color,
                            fill_opacity: newFeature.properties.fill_opacity,
                        }
                    };
                }
                return shape;
            });
        } else {
            customShapes.push({
                name: newFeature.properties?.name ?? `New Shape from Code ${index + 1}`,
                description: newFeature.properties.description,
                geojson: {
                    type: newFeature.geometry.type,
                    coordinates: newFeature.geometry.coordinates ?? newFeature.geometry.geometries,
                },
                style: {
                    stroke_color: newFeature.properties.stroke_color,
                    stroke_weight: newFeature.properties.stroke_weight,
                    stroke_opacity: newFeature.properties.stroke_opacity,
                    fill_color: newFeature.properties.fill_color,
                    fill_opacity: newFeature.properties.fill_opacity,
                }
            });
        }
    });

    return customShapes;
}

